import { useEffect, useRef, useState } from "react";

/** 告知后开始录音,停止后走同一投递管线。离开页面会停止并取消投递。 */
export function OnsiteRecorder(props: { consent: boolean; notice: string; onRecorded: (file: File) => void; disabled: boolean }): React.JSX.Element {
  const [recording, setRecording] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [err, setErr] = useState("");
  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const mounted = useRef(true);
  const cancelled = useRef(false);
  const callback = useRef(props.onRecorded);
  callback.current = props.onRecorded;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false; cancelled.current = true;
      if (recorder.current?.state === "recording") recorder.current.stop();
      stream.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);
  useEffect(() => {
    if (!recording) return;
    const timer = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, [recording]);
  const start = async (): Promise<void> => {
    if (requesting) return;
    setErr("");
    if (!props.consent || !props.notice) { setErr("开始录音前,请先向对方宣读告知并勾选确认"); return; }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setErr("当前浏览器不支持现场录音,请使用最新版浏览器或上传已有录音文件"); return;
    }
    setRequesting(true);
    try {
      cancelled.current = false;
      const media = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!mounted.current) { media.getTracks().forEach((t) => t.stop()); return; }
      stream.current = media;
      const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find((m) => MediaRecorder.isTypeSupported(m));
      const r = new MediaRecorder(media, mime ? { mimeType: mime } : undefined);
      recorder.current = r;
      const parts: Blob[] = []; let size = 0;
      r.ondataavailable = (event) => {
        if (event.data.size) { parts.push(event.data); size += event.data.size; }
        if (size > 100 * 1024 * 1024 && r.state === "recording") r.stop();
      };
      r.onerror = () => {
        cancelled.current = true; media.getTracks().forEach((t) => t.stop());
        if (mounted.current) { setRecording(false); setErr("录音被中断,请检查麦克风并重试"); }
      };
      r.onstop = () => {
        media.getTracks().forEach((t) => t.stop());
        if (!mounted.current || cancelled.current) return;
        setRecording(false);
        const type = r.mimeType || mime || "audio/webm";
        const blob = new Blob(parts, { type });
        if (!blob.size) { setErr("没有录到音频,请检查麦克风"); return; }
        callback.current(new File([blob], `现场录音-${new Date().toISOString().replace(/[:.]/g, "-")}.${type.includes("mp4") ? "mp4" : "webm"}`, { type }));
      };
      r.start(1000); setSeconds(0); setRecording(true);
    } catch {
      stream.current?.getTracks().forEach((t) => t.stop());
      if (mounted.current) setErr("无法使用麦克风。请允许麦克风权限,或上传已有录音");
    } finally { if (mounted.current) setRequesting(false); }
  };
  return <div className="card" style={{ padding: 12 }}>
    <b>🎙 现场录音</b>
    <p className="muted">先告知对方,再开始录音。停止后自动上传、转写并更新客户进展。</p>
    {recording ? <div className="row"><span role="status">🔴 {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")}</span>
      <button className="b p" onClick={() => recorder.current?.stop()}>停止并投递</button>
      <button className="b q" onClick={() => { cancelled.current = true; recorder.current?.stop(); stream.current?.getTracks().forEach((t) => t.stop()); setRecording(false); }}>取消录音</button>
    </div> : <button className="b p" disabled={!props.consent || !props.notice || props.disabled || requesting} onClick={() => void start()}>
      {requesting ? "等待麦克风授权…" : "开始现场录音"}</button>}
    {err && <p className="err" role="alert">{err}</p>}
  </div>;
}
